import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import FeatureBannersService from '../../services/featureBannersService';
import { showMsg } from '../../services/library';
import { BG_COLOR } from '../../constants/commonConst';

const REQ_FEATUREBANNERS = 'featurebanners/';

export const fetchBannersInfoReq = createAsyncThunk(
  `${REQ_FEATUREBANNERS}fetchBannerList`,

  async (_, { rejectWithValue }) => {
    try {
      const response = await FeatureBannersService.getBannerTemplates();
      if (response?.status) {
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const saveBannerReq = createAsyncThunk(
  `${REQ_FEATUREBANNERS}saveBannerReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await FeatureBannersService.addBannerTemplate(data);
      if (response?.status) {
        return response?.data[0] || null;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const deleteBannerReq = createAsyncThunk(
  `${REQ_FEATUREBANNERS}deleteBannerReq`,

  async (data, { rejectWithValue }) => {
    try {
      const response = await FeatureBannersService.deleteBannerTemplate(data);
      if (response?.status) {
        showMsg(response.message, 'success');
        return;
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

const bannerSettingsSlice = createSlice({
  name: 'featureBanners',
  initialState: {
    bannersList: [],
    selectedBannerId: null,
    tempName: '',

    bgType: BG_COLOR,
    bgColor: '#007bff',
    bgImageFile: null,
    bgFileName: '',
    bgImage: null,
    fontColor: '#000000',
    fontFileList: [],
    fontFile: null,
    bannerHeight: 30,
    bannerPosition: 'Top'
  },

  reducers: {
    handleBannersList: {
      reducer(state, action) {
        state.bannersList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSelectedBannerId: {
      reducer(state, action) {
        state.selectedBannerId = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTempName: {
      reducer(state, action) {
        state.tempName = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBgType: {
      reducer(state, action) {
        state.bgType = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBgColor: {
      reducer(state, action) {
        state.bgColor = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBgImageFile: {
      reducer(state, action) {
        state.bgImageFile = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBgFileName: {
      reducer(state, action) {
        state.bgFileName = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBgImage: {
      reducer(state, action) {
        state.bgImage = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFontColor: {
      reducer(state, action) {
        state.fontColor = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFontFileList: {
      reducer(state, action) {
        state.fontFileList = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFontFile: {
      reducer(state, action) {
        state.fontFile = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBannerHeight: {
      reducer(state, action) {
        state.bannerHeight = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBannerPosition: {
      reducer(state, action) {
        state.bannerPosition = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    }
  },

  extraReducers: builder => {
    builder
      // #region fetchBannersInfoReq
      .addCase(fetchBannersInfoReq.fulfilled, (state, action) => {
        const { featureBanners, fontFileList } = action.payload;
        state.bannersList =
          featureBanners?.map(d => ({
            ...d,
            label: d.fb_name,
            value: d.fb_id
          })) || [];

        state.fontFileList =
          fontFileList?.map(d => ({
            ...d,
            label: d.vf_filename,
            value: d.vf_id
          })) || [];

        state.selectedBannerId = null;
        state.tempName = '';
      })
      // #endregion

      // #region saveBannerReq
      .addCase(saveBannerReq.fulfilled, (state, action) => {
        const newAdded = action.payload;
        const msg = newAdded?.banner_id === 0 ? 'added' : 'updated';
        const updatedItem = { ...newAdded, label: newAdded.fb_name, value: newAdded.fb_id };
        const updatedList = state?.bannersList?.some(x => x.fb_id === newAdded.fb_id)
          ? state?.bannersList.map(item => (item.fb_id === newAdded.fb_id ? updatedItem : item))
          : [...state.bannersList, updatedItem];

        state.bannersList = updatedList;
        state.tempName = '';
        state.selectedBannerId = newAdded?.fb_id || null;
        showMsg(`Template ${msg} successfully.`, 'success');
      })
      // #endregion

      // #region deleteBannerReq
      .addCase(deleteBannerReq.fulfilled, state => {
        state.bannersList = state.bannersList?.filter(item => item.value !== state?.selectedBannerId);
        state.selectedBannerId = null;
      });
    // #endregion
  }
});

export const {
  handleBannersList,
  handleSelectedBannerId,
  handleTempName,
  handleBgType,
  handleBgColor,
  handleBgImageFile,
  handleBgFileName,
  handleBgImage,
  handleFontColor,
  handleFontFileList,
  handleFontFile,
  handleBannerHeight,
  handleBannerPosition
} = bannerSettingsSlice.actions;

export default bannerSettingsSlice.reducer;
